import type { AvisoDeImportacion } from '../api/importacion'

/**
 * Los avisos de una importación, **por causa y no por producto**. Diecinueve productos
 * con la misma imagen rota eran diecinueve frases iguales, cada una con su dirección
 * dentro; ahora son una caja: «En 19 productos: la imagen no se ha descargado…», con la
 * lista de productos y de direcciones plegada debajo.
 *
 * El núcleo escribe el mismo `texto` para la misma causa y deja lo concreto en `dato`
 * (src/importacion/avisos.ts): eso es lo que se agrupa. Un informe viejo que quede en el
 * historial del navegador trae frases sueltas; cada frase es su propio grupo.
 */
export type ProductoConAvisos = { id: number; nombre: string; avisos: Array<AvisoDeImportacion | string> }
export type GrupoDeAvisos = { texto: string; productos: Array<{ id: number; nombre: string }>; datos: string[] }

export const agruparAvisos = (creados: ProductoConAvisos[]): GrupoDeAvisos[] => {
  const grupos = new Map<string, GrupoDeAvisos & { orden: number }>()
  for (const producto of creados) {
    for (const aviso of producto.avisos) {
      const texto = typeof aviso === 'string' ? aviso : aviso.texto
      const dato = typeof aviso === 'string' ? undefined : aviso.dato
      if (!texto) continue
      let grupo = grupos.get(texto)
      if (!grupo) {
        grupo = { texto, productos: [], datos: [], orden: grupos.size }
        grupos.set(texto, grupo)
      }
      if (!grupo.productos.some((p) => p.id === producto.id)) grupo.productos.push({ id: producto.id, nombre: producto.nombre })
      if (dato && !grupo.datos.includes(dato)) grupo.datos.push(dato)
    }
  }
  // Lo que más productos toca, primero; a igualdad, en el orden en que apareció.
  return [...grupos.values()].sort((a, b) => b.productos.length - a.productos.length || a.orden - b.orden).map(({ texto, productos, datos }) => ({ texto, productos, datos }))
}

/** «https://tienda.pe/wp-content/uploads/2025/05/UPS%20Kaise.jpg» → «tienda.pe/…/UPS Kaise.jpg»; si sigue largo, se corta por el principio del fichero. */
export const direccionCorta = (url: string, maximo = 60): string => {
  let direccion: URL
  try {
    direccion = new URL(url)
  } catch {
    return cortar(url, maximo)
  }
  if (direccion.protocol !== 'http:' && direccion.protocol !== 'https:') return cortar(url, maximo)
  const tramos = direccion.pathname.split('/').filter(Boolean)
  const decodificar = (t: string) => {
    try {
      return decodeURIComponent(t)
    } catch {
      return t
    }
  }
  const host = direccion.host
  const fichero = tramos.length > 0 ? decodificar(tramos.at(-1)!) : ''
  if (!fichero) return cortar(host, maximo)
  const enMedio = tramos.length > 1 ? '/…/' : '/'
  const entero = `${host}${enMedio}${fichero}`
  if (entero.length <= maximo) return entero
  // Lo que distingue una imagen de otra suele estar al final del nombre (-1.jpg, -300x300.webp): se deja el final.
  const cabe = maximo - host.length - enMedio.length - 1
  if (cabe < 8) return cortar(entero, maximo)
  return `${host}${enMedio}…${fichero.slice(-cabe)}`
}

/** Un texto largo, cortado con «…» al final. */
const cortar = (texto: string, maximo: number): string => (texto.length <= maximo ? texto : `${texto.slice(0, Math.max(1, maximo - 1))}…`)

/** Lo concreto de un aviso, para enseñarlo en poco sitio: una dirección se acorta; el resto se corta si es muy largo. */
export const datoCorto = (dato: string, maximo = 60): string => (/^https?:\/\//i.test(dato.trim()) ? direccionCorta(dato.trim(), maximo) : cortar(dato, maximo * 2))

/** Para seguir una frase: «La imagen…» → «la imagen…». Una sigla («SKU…») se queda como está. */
export const enMinuscula = (texto: string): string => (/^\p{Lu}\p{Ll}/u.test(texto) ? `${texto.charAt(0).toLowerCase()}${texto.slice(1)}` : texto)

/** «En 1 producto» / «En 19 productos». */
export const enCuantos = (n: number): string => `En ${n} ${n === 1 ? 'producto' : 'productos'}`

/** Lo que se enseña de una lista larga, y cuántos quedan fuera. */
export const primeros = <T>(lista: T[], tope = 50): { visibles: T[]; resto: number } => ({ visibles: lista.slice(0, tope), resto: Math.max(0, lista.length - tope) })
